import { createAction } from "@logicsoftware/redux-toolkit";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { appUrls } from "~/EasyProjectsCommon/AppUrls";
import { OAuthClient } from "~/EasyProjectsCommon/OAuthClient";
import { MessageBoardActionTypes } from "~/messageBoard/const/messageBoardActionTypes";
import { ApiErrorTypes } from "~/Redux/Actions";
import { callApi } from "~/Redux/Sagas/effects/callApi";
import { AiCompletionKnownErrors } from "../const";
import { AiCompletionState, AiTemplates, AiTones } from "../types";
import { setAiCompletion, setAiSuggestion } from "./reducers";
import { getAiCompletion } from "./selectors";

type ActionWithCallback = { callback: () => void };
type AiRequestPayload = AiCompletionState["context"] & ActionWithCallback;

export const requestAiCompletion = createAction<AiRequestPayload>(MessageBoardActionTypes.REQUEST_AI_COMPLETION);
export const retryAiCompletion = createAction<ActionWithCallback>(MessageBoardActionTypes.RETRY_AI_COMPLETION);

export function* watchAiRequests() {
    yield takeLatest(requestAiCompletion, handleAiRequest);
    yield takeLatest(retryAiCompletion, handleAiRetry);
}

function* handleAiRequest(action: ReturnType<typeof requestAiCompletion>) {
    const { prompt, template, tone, callback } = action.payload;

    try {
        const suggestion: string = yield requestSuggestion(prompt, template, tone);
        yield put(setAiCompletion({ suggestion, context: { prompt, template, tone } }));
    } finally {
        yield call(callback);
    }
}

function* handleAiRetry({ payload: { callback } }: ReturnType<typeof retryAiCompletion>) {
    const {
        context: { prompt, template, tone },
    } = yield select(getAiCompletion);

    try {
        const suggestion: string = yield requestSuggestion(prompt, template, tone);
        yield put(setAiSuggestion(suggestion));
    } finally {
        yield call(callback);
    }
}

export type AiServerResponse = { Text: string };
function* requestSuggestion(prompt: string, template: AiTemplates, tone?: AiTones) {
    const response: AiServerResponse = yield callApi(
        ({ ajaxOptions }) => OAuthClient.postJson(appUrls.ai.completion, { prompt, template, tone }, ajaxOptions),
        {
            getErrorOptions: (errorType, body) =>
                errorType === ApiErrorTypes.UserFriendlyError &&
                (body.ErrorKey === AiCompletionKnownErrors.RateLimitExceededByAccount ||
                    body.ErrorKey === AiCompletionKnownErrors.RateLimitExceededByUser) && { withoutReInit: true },
        }
    );

    return response.Text;
}
